import type {
  ApprovalStateDto,
  DashboardDto,
  DecideResultDto,
  DepartmentDto,
  DispatchSummaryDto,
  AutomationListDto,
  ReassignOwnerResultDto,
  SharePointSync,
  AttachmentDto,
  ProcessInterviewDto,
  PersonDto,
  PersonSearchResultDto,
  AppRoleName,
  NotificationDto,
  TeamsConnectResultDto,
  VideoDto,
  VideoUploadDto,
  TeamsTestResultDto,
  FlagDto,
  QueueItemDto,
  SubmitResultDto,
  VersionBump,
  DiffResponseDto,
  GraphResponseDto,
  RevisionResponseDto,
  InterviewDto,
  InterviewEventDto,
  InterviewStartResponseDto,
  MeResponse,
  ProblemDto,
  ProcessDetailDto,
  ProcessListItemDto,
  ProcessGraph,
  PublishResultDto,
  SaveGraphResponseDto,
} from '@process-hub/shared';
import { getAccessToken } from './auth';
import { config } from './config';
import { NetworkError } from './errors';
import { readSse } from './sse';

/** Typed client for the Process Hub API. Every call carries the bearer token. */

export class ApiError extends Error {
  constructor(
    public readonly problem: ProblemDto,
    message = problem.detail ? `${problem.title}: ${problem.detail}` : problem.title,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get status(): number {
    return this.problem.status;
  }
}

/**
 * Every call goes through here so a request that never reached the API is reported as one.
 * The browser turns a CORS-blocked response — which is what Easy Auth's 401 is, since it is
 * written before the Functions CORS layer runs — into an opaque TypeError. D-055.
 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    throw new NetworkError(e instanceof Error ? e.message : String(e));
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body) headers.set('Content-Type', 'application/json');

  const res = await send(`${config.apiBaseUrl}/api${path}`, { ...init, headers });
  if (res.ok) return (await res.json()) as T;

  let problem: ProblemDto;
  try {
    problem = (await res.json()) as ProblemDto;
  } catch {
    problem = { type: 'about:blank', title: res.statusText || 'Request failed', status: res.status };
  }
  throw new ApiError(problem);
}

/** POSTs one interview message and yields the server-sent events as they arrive. `spoken` records voice capture on the session (D-032). */
function streamTurn(sessionId: string, message: string, spoken = false): AsyncGenerator<InterviewEventDto> {
  return streamPost(`/interviews/${encodeURIComponent(sessionId)}/turns`, { message, spoken });
}

async function toBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** Uploads an existing SOP as the owner's first turn (D-030); same event stream as a turn, preceded by a `document` event. */
async function* streamDocument(sessionId: string, file: File): AsyncGenerator<InterviewEventDto> {
  yield* streamPost(`/interviews/${encodeURIComponent(sessionId)}/document`, { fileName: file.name, contentType: file.type || 'application/octet-stream', base64: await toBase64(file) });
}

/** A screenshot pasted or dropped into the interview (D-033): stored on the process, then one turn in which the interviewer sees it. */
async function* streamScreenshot(sessionId: string, file: Blob, fileName: string, note: string | null): AsyncGenerator<InterviewEventDto> {
  yield* streamPost(`/interviews/${encodeURIComponent(sessionId)}/screenshot`, { fileName, contentType: file.type || 'image/png', base64: await toBase64(file), note });
}

/** Image bytes behind the bearer token; the caller turns the Blob into an object URL. */
async function attachmentBlob(slug: string, id: string): Promise<Blob> {
  const token = await getAccessToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await send(`${config.apiBaseUrl}/api/processes/${encodeURIComponent(slug)}/attachments/${encodeURIComponent(id)}/content`, { headers });
  if (!res.ok) throw new ApiError({ type: 'about:blank', title: res.statusText || 'Request failed', status: res.status });
  return res.blob();
}

async function* streamPost(path: string, payload: unknown): AsyncGenerator<InterviewEventDto> {
  const token = await getAccessToken();
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await send(`${config.apiBaseUrl}/api${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) {
    let problem: ProblemDto;
    try {
      problem = (await res.json()) as ProblemDto;
    } catch {
      problem = { type: 'about:blank', title: res.statusText || 'Request failed', status: res.status };
    }
    throw new ApiError(problem);
  }
  for await (const e of readSse(res.body)) {
    const data = JSON.parse(e.data) as Record<string, unknown>;
    yield { ...data, type: e.event } as InterviewEventDto;
  }
}

export const api = {
  me: () => request<MeResponse>('/me'),
  listDepartments: () => request<DepartmentDto[]>('/departments'),
  connectTeams: (oid: string) => request<TeamsConnectResultDto>(`/people/${encodeURIComponent(oid)}/teams`, { method: 'POST' }),
  connectAllTeams: () => request<TeamsConnectResultDto[]>('/people/teams/connect-all', { method: 'POST' }),
  testTeams: (oid: string) => request<TeamsTestResultDto>(`/people/${encodeURIComponent(oid)}/teams/test`, { method: 'POST' }),
  listNotifications: (limit = 50) => request<NotificationDto[]>(`/notifications?limit=${limit}`),
  dispatchNotifications: () => request<DispatchSummaryDto>('/notifications/dispatch', { method: 'POST' }),
  deleteProcess: (slug: string) => request<{ deleted: boolean; slug: string; name: string }>(`/processes/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  restoreProcess: (slug: string, reason: string | null) =>
    request<{ restored: boolean; status: string; republished: string[]; notRepublished: string[] }>(`/processes/${encodeURIComponent(slug)}/restore`, { method: 'POST', body: JSON.stringify({ reason }) }),
  mePhoto: async (): Promise<Blob | null> => {
    try {
      const token = await getAccessToken();
      const headers = new Headers();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      const res = await send(`${config.apiBaseUrl}/api/me/photo`, { headers });
      return res.ok ? res.blob() : null;
    } catch {
      return null; // The avatar is decorative; never let it raise.
    }
  },
  listPeople: () => request<PersonDto[]>('/people'),
  searchPeople: (q: string) => request<PersonSearchResultDto[]>(`/people/search?q=${encodeURIComponent(q)}`),
  setRoles: (oid: string, roles: AppRoleName[]) => request<PersonDto>(`/people/${encodeURIComponent(oid)}/roles`, { method: 'PUT', body: JSON.stringify({ roles }) }),
  retireProcess: (slug: string, reason: string | null) => request<{ retired: boolean; sharePoint: SharePointSync }>(`/processes/${encodeURIComponent(slug)}/retire`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getProcessInterview: (slug: string) => request<ProcessInterviewDto | null>(`/processes/${encodeURIComponent(slug)}/interview`),
  resumeInterview: (slug: string) => request<{ sessionId: string; resumed: boolean; created: boolean }>(`/processes/${encodeURIComponent(slug)}/interview`, { method: 'POST' }),
  reassignOwner: (slug: string, user: string) => request<ReassignOwnerResultDto>(`/processes/${encodeURIComponent(slug)}/owner`, { method: 'PUT', body: JSON.stringify({ user }) }),
  automation: () => request<AutomationListDto>('/automation'),
  proposeAutomation: (processId: string, logicalId: string) => request<{ status: string }>(`/automation/${processId}/${logicalId}/propose`, { method: 'POST', body: '{}' }),
  decideAutomation: (processId: string, logicalId: string, decision: 'Accepted' | 'Declined', comment: string | null) =>
    request<{ status: string }>(`/automation/${processId}/${logicalId}/decide`, { method: 'POST', body: JSON.stringify({ decision, comment }) }),
  shipAutomation: (processId: string, logicalId: string) => request<{ status: string }>(`/automation/${processId}/${logicalId}/ship`, { method: 'POST' }),
  withdrawAutomation: (processId: string, logicalId: string) => request<{ status: string }>(`/automation/${processId}/${logicalId}/withdraw`, { method: 'POST' }),
  listProcesses: (includeStale = false) => request<ProcessListItemDto[]>(`/processes${includeStale ? '?includeStale=true' : ''}`),
  getProcess: (slug: string) => request<ProcessDetailDto>(`/processes/${encodeURIComponent(slug)}`),
  getGraph: (slug: string, label = 'current') =>
    request<GraphResponseDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/graph`),
  createRevision: (slug: string) => request<RevisionResponseDto>(`/processes/${encodeURIComponent(slug)}/revisions`, { method: 'POST' }),
  submitVersion: (slug: string, label: string, bump?: Exclude<VersionBump, 'none'>) =>
    request<SubmitResultDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/submit`, { method: 'POST', body: JSON.stringify(bump ? { bump } : {}) }),
  getApproval: (slug: string, label: string) => request<ApprovalStateDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/approval`),
  decide: (slug: string, label: string, decision: 'Approved' | 'ChangesRequested' | 'Rejected', comment: string | null) =>
    request<DecideResultDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/decisions`, { method: 'POST', body: JSON.stringify({ decision, comment }) }),
  approvalQueue: () => request<QueueItemDto[]>('/approvals'),
  publish: (slug: string, label: string, svg: string) =>
    request<PublishResultDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/publish`, { method: 'POST', body: JSON.stringify({ svg }) }),
  dashboard: () => request<DashboardDto>('/dashboard'),
  listFlags: (slug: string) => request<FlagDto[]>(`/processes/${encodeURIComponent(slug)}/flags`),
  addFlag: (slug: string, reason: string) => request<FlagDto>(`/processes/${encodeURIComponent(slug)}/flags`, { method: 'POST', body: JSON.stringify({ reason }) }),
  resolveFlag: (slug: string, id: string) => request<{ resolved: boolean }>(`/processes/${encodeURIComponent(slug)}/flags/${encodeURIComponent(id)}/resolve`, { method: 'POST' }),
  recertify: (slug: string, comment: string | null) => request<{ nextRecertDate: string; sharePoint: SharePointSync }>(`/processes/${encodeURIComponent(slug)}/recertify`, { method: 'POST', body: JSON.stringify({ comment }) }),
  getDiff: (slug: string, label: string, against?: string) =>
    request<DiffResponseDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/diff${against ? `?against=${encodeURIComponent(against)}` : ''}`),
  saveGraph: (slug: string, label: string, graph: ProcessGraph) =>
    request<SaveGraphResponseDto>(`/processes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(label)}/graph`, { method: 'PUT', body: JSON.stringify(graph) }),
  streamDocument,
  streamScreenshot,
  attachmentBlob,
  listAttachments: (slug: string, label?: string) => request<AttachmentDto[]>(`/processes/${encodeURIComponent(slug)}/attachments${label ? `?version=${encodeURIComponent(label)}` : ''}`),
  uploadAttachment: async (slug: string, file: File, stepKey: string | null, caption: string | null = null) =>
    request<AttachmentDto>(`/processes/${encodeURIComponent(slug)}/attachments`, { method: 'POST', body: JSON.stringify({ fileName: file.name, contentType: file.type, base64: await toBase64(file), stepKey, caption }) }),
  setAttachmentStep: (slug: string, id: string, stepKey: string | null) => request<AttachmentDto>(`/processes/${encodeURIComponent(slug)}/attachments/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ stepKey }) }),
  listVideos: (slug: string) => request<VideoDto[]>(`/processes/${encodeURIComponent(slug)}/videos`),
  startVideoUpload: (slug: string, body: { title: string; fileName: string; contentType: string; bytes: number }) => request<VideoUploadDto>(`/processes/${encodeURIComponent(slug)}/videos`, { method: 'POST', body: JSON.stringify(body) }),
  completeVideoUpload: (slug: string, id: string) => request<VideoDto>(`/processes/${encodeURIComponent(slug)}/videos/${encodeURIComponent(id)}/complete`, { method: 'POST' }),
  renameVideo: (slug: string, id: string, title: string) => request<{ renamed: boolean }>(`/processes/${encodeURIComponent(slug)}/videos/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  deleteVideo: (slug: string, id: string) => request<{ deleted: boolean }>(`/processes/${encodeURIComponent(slug)}/videos/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  deleteAttachment: (slug: string, id: string) => request<{ deleted: boolean }>(`/processes/${encodeURIComponent(slug)}/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  startInterview: (body: { departmentId: string; name: string; maintainerRole: string }) =>
    request<InterviewStartResponseDto>('/interviews', { method: 'POST', body: JSON.stringify(body) }),
  getInterview: (id: string) => request<InterviewDto>(`/interviews/${encodeURIComponent(id)}`),
  finishInterview: (id: string) => request<{ slug: string; completed: boolean }>(`/interviews/${encodeURIComponent(id)}/finish`, { method: 'POST' }),
  streamTurn,
};
